import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { extractNeedRequirements } from '@/lib/recruitmentApi';
import apiClient from '@/lib/api';
import CompanySelectCombobox from '@/components/admin/recruitment/CompanySelectCombobox';

const blank = {
  title: '',
  client_id: '',
  description: '',
  location: '',
  seniority: '',
  experience_years: '',
  constraints: '',
  start_date: '',
  end_date: '',
  mandatory: '',
  recommended: '',
  bonus: '',
  functional: '',
  responsibilities: '',
  context_technologies: '',
};

const split = (value) => (value || '').split(',').map((s) => s.trim()).filter(Boolean);
const inputClass =
  'w-full min-h-11 rounded-lg bg-slate-900 border border-white/20 px-3 py-2 text-sm text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400';

export default function NeedCreateModal({
  isOpen,
  onClose,
  onCreateNeed,
  onCreated,
}) {
  const [rawText, setRawText] = useState('');
  const [form, setForm] = useState(blank);
  const [contracts, setContracts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [analysisError, setAnalysisError] = useState('');
  const [edited, setEdited] = useState(false);

  // Commercial terms & company selection
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [tjmClient, setTjmClient] = useState('');
  const [tjmClientMode, setTjmClientMode] = useState('unknown'); // "unknown" | "fixed" | "max"
  const [freeDays, setFreeDays] = useState(0); // Non-negative integer, default 0
  const [missionType, setMissionType] = useState('long'); // "long" | "court"
  const [isStartDateProvisional, setIsStartDateProvisional] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    apiClient
      .get('/recruitment/companies')
      .then((res) => {
        setCompanies(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setCompanies([]));
  }, [isOpen]);

  if (!isOpen) return null;

  const analyze = async () => {
    setBusy(true);
    setAnalysisError('');
    setError('');
    try {
      const data = await extractNeedRequirements(rawText, form.title);
      setExtracted(data);
      setForm((previous) => {
        const next = { ...previous };
        for (const key of Object.keys(blank)) {
          if (key === 'title') {
            // RÈGLE MÉTIER ABSOLUE : ne JAMAIS écraser un intitulé déjà saisi manuellement
            if (!next.title || !next.title.trim()) {
              if (data.title) next.title = String(data.title);
            }
            continue;
          }
          if (Array.isArray(data[key])) {
            next[key] = data[key].join(', ');
          } else if (data[key] !== null && data[key] !== undefined) {
            next[key] = String(data[key]);
          }
        }
        return next;
      });
      setEdited(false);
      setExpanded(true);
    } catch {
      setAnalysisError(
        "L’analyse IA n’a pas abouti. Vous pouvez compléter les informations manuellement et créer le besoin."
      );
      setExpanded(true);
    } finally {
      setBusy(false);
    }
  };

  const handleSelectCompany = (compId, companyObj) => {
    setSelectedCompanyId(compId);
    if (companyObj) {
      setForm((prev) => ({ ...prev, client_id: companyObj.name }));
    } else {
      setForm((prev) => ({ ...prev, client_id: '' }));
      setSelectedContactId('');
    }
  };

  const handleCompanyCreated = (newComp) => {
    setCompanies((prev) => [newComp, ...prev.filter((c) => (c.id || c._id) !== (newComp.id || newComp._id))]);
    setSelectedCompanyId(newComp.id || newComp._id);
    setForm((prev) => ({ ...prev, client_id: newComp.name }));
  };

  const handleContactCreated = (newContact, compId) => {
    setCompanies((prev) =>
      prev.map((comp) => {
        if ((comp.id || comp._id) === compId) {
          const existingContacts = Array.isArray(comp.contacts) ? comp.contacts : [];
          return { ...comp, contacts: [...existingContacts, newContact] };
        }
        return comp;
      })
    );
    setSelectedContactId(newContact.id || newContact._id);
  };

  const selectedCompany = companies.find((c) => (c.id || c._id) === selectedCompanyId);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!form.title.trim()) {
      setExpanded(true);
      setError("Renseignez l’intitulé du poste avant de créer le besoin.");
      return;
    }

    if (missionType === 'court' && !form.end_date) {
      setExpanded(true);
      setError("Pour une mission courte, la date de fin est obligatoire pour le calcul sur la période réelle.");
      return;
    }

    const freeDaysInt = Math.max(0, parseInt(freeDays, 10) || 0);

    setSubmitting(true);
    try {
      const requirements = Object.fromEntries(
        ['mandatory', 'recommended', 'bonus', 'functional', 'responsibilities', 'context_technologies'].map((key) => [
          key,
          split(form[key]),
        ])
      );
      if (extracted?.role_identified) {
        requirements.role_identified = extracted.role_identified;
      }
      const created = await onCreateNeed({
        title: form.title.trim(),
        client_id: form.client_id.trim() || null,
        company_id: selectedCompanyId || null,
        company_name: form.client_id.trim() || null,
        company_type: selectedCompany?.company_type || (selectedCompanyId ? 'client' : null),
        client_contact_id: selectedContactId || null,
        tjm_client: tjmClient && tjmClientMode !== 'unknown' ? parseFloat(tjmClient) : null,
        tjm_client_mode: tjmClientMode,
        free_days: freeDaysInt,
        mission_type: missionType,
        is_start_date_provisional: Boolean(isStartDateProvisional),
        commercial_terms: {
          tjm_client: tjmClient && tjmClientMode !== 'unknown' ? parseFloat(tjmClient) : null,
          mode: tjmClientMode,
          free_days: freeDaysInt,
          mission_type: missionType,
        },
        description: form.description.trim() || rawText.trim(),
        description_raw: rawText.trim() || null,
        location: form.location || null,
        seniority: form.seniority || null,
        experience_years: form.experience_years || null,
        constraints: form.constraints || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        contract_types: contracts,
        required_skills: requirements.mandatory,
        recommended_skills: requirements.recommended,
        bonus_skills: requirements.bonus,
        requirements,
        requirements_source:
          extracted && !edited ? extracted.requirements_source : 'manual',
        input_mode: rawText.trim() ? 'quick' : 'structured',
        status: 'open',
      });
      onClose();
      onCreated?.(created);
    } catch (err) {
      setError(
        typeof err?.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'La création du besoin a échoué. Vos informations sont conservées.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !busy && !submitting) onClose();
      }}
    >
      <DialogContent
        closeDisabled={busy || submitting}
        className="max-w-3xl bg-[#101426] text-white border-white/10 rounded-2xl w-[calc(100%_-_2rem)] max-h-[90vh] overflow-y-auto"
      >
        <DialogTitle className="pr-10 text-lg font-bold">Nouveau besoin client</DialogTitle>
        <DialogDescription className="text-xs text-slate-400">
          Collez le descriptif pour analyse IA ou saisissez directement les informations, entreprise et conditions financières.
        </DialogDescription>
        <form onSubmit={submit} className="space-y-5">
          <label className="block space-y-2">
            <span className="text-xs font-semibold text-slate-300">
              Collez ici le descriptif de votre besoin client
            </span>
            <textarea
              rows={5}
              className={inputClass}
              value={rawText}
              placeholder="Ex: Développeur React / Node.js senior à Rennes, démarrage immédiat..."
              onChange={(e) => {
                setRawText(e.target.value);
                setExtracted(null);
                setEdited(true);
              }}
              disabled={busy || submitting}
            />
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="min-h-10 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white disabled:opacity-50"
              onClick={analyze}
              disabled={!rawText.trim() || busy || submitting}
            >
              {busy ? 'Analyse en cours…' : 'Analyser avec l’IA'}
            </button>
            <span className="text-xs text-slate-400">
              Extraction automatique des compétences, de la localisation et des contraintes.
            </span>
          </div>
          {analysisError && (
            <p role="alert" className="text-amber-300 text-xs">
              {analysisError}
            </p>
          )}
          {extracted && (
            <div
              role="status"
              className="p-3 border border-blue-400/30 bg-blue-500/10 rounded-lg text-xs text-blue-200"
            >
              {extracted.extraction_mode === 'llm'
                ? 'Analyse terminée. Vérifiez et corrigez les critères extraits ci-dessous.'
                : 'L’IA est indisponible. Une lecture heuristique a été effectuée : vérifiez et complétez.'}
              {extracted.ambiguities?.length > 0 && (
                <div className="mt-2 text-amber-300">
                  <p className="font-semibold">Mentions ambiguës détectées :</p>
                  <ul className="list-disc pl-4 mt-1">
                    {extracted.ambiguities.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Section 1: Entreprise & Interlocuteur (Recherchable, anti-doublon, création sans perte) */}
          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-4">
            <CompanySelectCombobox
              companies={companies}
              selectedCompanyId={selectedCompanyId}
              onSelectCompany={handleSelectCompany}
              selectedContactId={selectedContactId}
              onSelectContact={setSelectedContactId}
              onCompanyCreated={handleCompanyCreated}
              onContactCreated={handleContactCreated}
              disabled={busy || submitting}
            />

            {/* Section 2: Type de Mission & Conditions Commerciales */}
            <div className="pt-3 border-t border-white/5 space-y-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-300 block">
                Type de mission & Conditions commerciales
              </span>

              {/* Type de Mission Selector */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-[11px] text-slate-400 mb-1 font-semibold">
                    Type de mission
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setMissionType('long')}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                        missionType === 'long'
                          ? 'bg-blue-600 text-white border-blue-500 shadow-sm'
                          : 'bg-slate-900 text-slate-300 border-white/10 hover:bg-white/5'
                      }`}
                      data-testid="mission-type-long-btn"
                    >
                      Mission longue
                      <span className="block text-[10px] font-normal opacity-80">Horizon 12 mois</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMissionType('court')}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold border transition-all ${
                        missionType === 'court'
                          ? 'bg-blue-600 text-white border-blue-500 shadow-sm'
                          : 'bg-slate-900 text-slate-300 border-white/10 hover:bg-white/5'
                      }`}
                      data-testid="mission-type-court-btn"
                    >
                      Mission courte
                      <span className="block text-[10px] font-normal opacity-80">Période ferme</span>
                    </button>
                  </div>
                </div>

                {/* Free Days field (Replaces visible commercial_terms text) */}
                <div>
                  <label htmlFor="need-free-days" className="block text-[11px] text-slate-400 mb-1 font-semibold">
                    Nombre de jours de gratuité
                  </label>
                  <input
                    id="need-free-days"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="0"
                    value={freeDays}
                    onChange={(e) => setFreeDays(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    className={inputClass}
                    data-testid="free-days-input"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Déduit une seule fois sur toute la durée de la mission (jamais chaque mois).
                  </p>
                </div>
              </div>

              {/* Financial parameters: TJM Client & Mode */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-[11px] text-slate-400 mb-1 font-semibold">
                    Mode TJM Client
                  </label>
                  <select
                    value={tjmClientMode}
                    onChange={(e) => setTjmClientMode(e.target.value)}
                    className={inputClass}
                    data-testid="tjm-mode-select"
                  >
                    <option value="unknown">Non renseigné</option>
                    <option value="fixed">TJM fixe</option>
                    <option value="max">TJM maximum</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-slate-400 mb-1 font-semibold">
                    Montant TJM Client (€/j)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="10"
                    placeholder="Ex: 600"
                    disabled={tjmClientMode === 'unknown'}
                    value={tjmClient}
                    onChange={(e) => setTjmClient(e.target.value)}
                    className={inputClass}
                    data-testid="tjm-client-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <details
            open={expanded}
            onToggle={(e) => setExpanded(e.currentTarget.open)}
            className="border border-white/15 rounded-xl p-4"
          >
            <summary className="cursor-pointer min-h-11 text-xs font-semibold text-slate-200">
              Critères détaillés du besoin (Poste, Dates, Compétences, Contrats)
            </summary>
            {extracted?.suggested_title && form.title && extracted.suggested_title !== form.title && (
              <div className="mt-2 p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300 flex items-center justify-between">
                <span>Suggestion IA de titre alternatif : <strong className="text-white">{extracted.suggested_title}</strong></span>
                <button
                  type="button"
                  onClick={() => {
                    setForm((prev) => ({ ...prev, title: extracted.suggested_title }));
                    setEdited(true);
                  }}
                  className="px-2 py-1 text-[11px] rounded bg-blue-600 hover:bg-blue-500 text-white font-medium ml-2"
                >
                  Appliquer
                </button>
              </div>
            )}
            {extracted?.role_identified && (
              <div className="mt-2 text-[11px] text-slate-400">
                Rôle analysé par l’IA : <span className="font-semibold text-purple-300">{extracted.role_identified}</span>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2 pt-3">
              {[
                ['title', 'Intitulé du poste *'],
                ['location', 'Localisation'],
                ['seniority', 'Séniorité'],
                ['experience_years', 'Expérience souhaitée'],
                ['responsibilities', 'Responsabilités principales'],
                ['mandatory', 'Technologies obligatoires'],
                ['recommended', 'Technologies nécessaires ou recommandées'],
                ['bonus', 'Technologies bonus'],
                ['context_technologies', 'Technologies de contexte'],
                ['functional', 'Compétences métier'],
                ['constraints', 'Contraintes particulières'],
              ].map(([key, label]) => (
                <label key={key} className="block space-y-1 text-xs text-slate-300">
                  <span className="font-medium">{label}</span>
                  <input
                    className={inputClass}
                    type="text"
                    value={form[key]}
                    disabled={busy || submitting}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        [key]: e.target.value,
                      });
                      setEdited(true);
                    }}
                  />
                </label>
              ))}

              {/* Start Date & Provisional checkbox */}
              <div className="space-y-1 text-xs text-slate-300">
                <label htmlFor="need-start-date" className="block font-medium">Date de démarrage souhaitée</label>
                <input
                  id="need-start-date"
                  data-testid="start-date-input"
                  className={inputClass}
                  type="date"
                  value={form.start_date}
                  disabled={busy || submitting}
                  onChange={(e) => {
                    setForm({ ...form, start_date: e.target.value });
                    setEdited(true);
                  }}
                />
                <label className="flex items-center gap-2 pt-1 text-[11px] text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isStartDateProvisional}
                    onChange={(e) => setIsStartDateProvisional(e.target.checked)}
                    className="rounded border-slate-700 bg-slate-900"
                  />
                  <span>Date de démarrage prévisionnelle à confirmer (non contractuelle)</span>
                </label>
              </div>

              {/* End Date (Mandatory for short mission) */}
              <div className="space-y-1 text-xs text-slate-300">
                <label htmlFor="need-end-date" className="block font-medium">
                  Date de fin contractuelle {missionType === 'court' && <strong className="text-rose-400">*</strong>}
                </label>
                <input
                  id="need-end-date"
                  data-testid="end-date-input"
                  className={inputClass}
                  type="date"
                  value={form.end_date}
                  disabled={busy || submitting}
                  onChange={(e) => {
                    setForm({ ...form, end_date: e.target.value });
                    setEdited(true);
                  }}
                />
                <p className="text-[10px] text-slate-500 pt-0.5">
                  {missionType === 'court'
                    ? 'Obligatoire pour les missions courtes (calcul sur la période réelle).'
                    : 'Facultatif pour les missions longues (calcul annuel sur 12 mois).'}
                </p>
              </div>

              <p className="text-[11px] text-slate-400 sm:col-span-2">
                Séparez les compétences par des virgules. Les compétences obligatoires servent au pré-filtrage strict ; les équivalences sont prises en compte lors du matching.
              </p>

              <fieldset className="sm:col-span-2 space-y-2">
                <legend className="text-xs text-slate-200 font-semibold">
                  Types de contrats (facultatif)
                </legend>
                <div className="flex flex-wrap gap-4">
                  {['CDI', 'Freelance', 'Portage salarial'].map((value) => (
                    <label key={value} className="flex items-center gap-2 min-h-10 text-xs">
                      <input
                        type="checkbox"
                        value={value}
                        checked={contracts.includes(value)}
                        disabled={submitting}
                        onChange={(e) =>
                          setContracts(
                            e.target.checked
                              ? [...contracts, value]
                              : contracts.filter((item) => item !== value)
                          )
                        }
                      />
                      {value}
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400">
                  Aucune sélection : ouvert à tous les types de contrats.
                </p>
              </fieldset>
            </div>
          </details>

          {error && (
            <p role="alert" className="text-rose-300 text-xs font-semibold">
              {error}
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-3 border-t border-white/10 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={busy || submitting}
              className="min-h-10 px-4 text-xs font-semibold text-slate-300 hover:text-white"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={busy || submitting}
              className="min-h-10 px-5 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-semibold text-white disabled:opacity-50"
            >
              {submitting ? 'Création…' : 'Créer le besoin'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
